import React, { useCallback, useEffect, useRef, useState } from 'react';

import { EChartsInstance, EChartsReactProps } from 'echarts-for-react';
import EChartsReactCore from 'echarts-for-react/lib/core';
import {
    BarChart,
    CustomChart,
    GraphChart,
    HeatmapChart,
    LineChart,
    MapChart,
    PieChart,
    ScatterChart,
    TreemapChart,
} from 'echarts/charts';
import { MapChart as EchartsMapChart } from 'echarts/charts';
import {
    DataZoomComponent,
    DatasetComponent,
    GeoComponent,
    GraphicComponent,
    GridComponent,
    LegendComponent,
    MarkLineComponent,
    TimelineComponent,
    TitleComponent,
    ToolboxComponent,
    TooltipComponent,
    VisualMapComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import SaveAsImage from 'echarts/lib/component/toolbox/feature/SaveAsImage';
import { CanvasRenderer } from 'echarts/renderers';
import Resize from 'element-resize-detector';
import { debounce } from 'lodash/fp';

echarts.use([
    BarChart,
    CustomChart,
    CanvasRenderer,
    DatasetComponent,
    DataZoomComponent,
    EchartsMapChart,
    GeoComponent,
    GraphChart,
    GraphicComponent,
    GridComponent,
    HeatmapChart,
    LegendComponent,
    LineChart,
    MapChart,
    MarkLineComponent,
    PieChart,
    TimelineComponent,
    TitleComponent,
    ToolboxComponent,
    TooltipComponent,
    TreemapChart,
    SaveAsImage,
    ScatterChart,
    VisualMapComponent,
]);

type Props = EChartsReactProps & {
    onChartResize?: (width: number, height: number) => void;
};

// ECharts measures and draws text once on canvas, so the first draw must wait for web fonts
const useAreFontsReady = () => {
    const [areFontsReady, setAreFontsReady] = useState(document.fonts.status === 'loaded');

    useEffect(() => {
        if (!areFontsReady) document.fonts.ready.then(() => setAreFontsReady(true));
    }, [areFontsReady]);

    return areFontsReady;
};

const EChartsWrapper: React.FC<Props> = ({ onChartResize, style, theme, ...props }) => {
    const areFontsReady = useAreFontsReady();
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<EChartsInstance | null>(null);
    const resizer = useRef<ReturnType<typeof Resize> | null>(null);

    const resizeHandler = useCallback(
        (width: number, height: number) => {
            onChartResize?.(width, height);
            chartInstance.current?.resize();
        },
        [onChartResize]
    );

    const onWindowResize = useCallback(
        debounce(250, () => {
            const instance = chartInstance.current;
            if (!instance || instance.isDisposed?.()) return;
            resizeHandler(instance.getWidth(), instance.getHeight());
        }),
        [resizeHandler]
    );

    useEffect(() => {
        window.addEventListener('resize', onWindowResize);
        resizer.current = Resize({ strategy: 'scroll' });

        if (chartRef.current) {
            resizer.current.listenTo(chartRef.current, (el) => {
                resizeHandler(el.offsetWidth, el.offsetHeight);
            });
        }

        return () => {
            window.removeEventListener('resize', onWindowResize);
            if (resizer.current && chartRef.current) {
                resizer.current.uninstall(chartRef.current);
            }
        };
    }, [onWindowResize, resizeHandler]);

    const onChartReady = useCallback(
        (instance: EChartsInstance) => {
            chartInstance.current = instance;
            props.onChartReady?.(instance);
        },
        [props.onChartReady]
    );

    return (
        <div
            ref={chartRef}
            style={{
                width: style?.width,
                height: isFinite(style?.height) ? style.height : 0,
            }}
        >
            {areFontsReady && (
                <EChartsReactCore
                    echarts={echarts}
                    notMerge
                    {...props}
                    {...{ style }}
                    theme={theme}
                    onChartReady={onChartReady}
                />
            )}
            <canvas id="canvasFontSizer" width="0" height="0" />
        </div>
    );
};

export default EChartsWrapper;
